import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryPasswordStep2 } from "@ui-kit/components/index.es";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import withExchangeToken from "pages/_components/withExchangeToken";
import * as i18n from "util/i18n";
import Yup from "yup";

const FORM_ID = "recoveryPassword.step2";

function RecoveryPasswordStep2V3({ dispatch, history, exchangeToken, recoveryAsyncPending, fieldErrors }) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryPassword"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const initialValues = useMemo(
        () => ({
            resetCode: "",
        }),
        [],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                resetCode: Yup.string().required(i18n.get(`${FORM_ID}.resetCode.required`)),
            }),
        [],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const code = values.resetCode ?? "";

            const enhancedBag = {
                ...formikBag,
                props: { ...(formikBag.props || {}), location: { pathname: "/v3/recoveryPassword/step2" }, history },
            };

            dispatch(recoveryPasswordActions.recoveryPassStep2(code, enhancedBag, exchangeToken));
        },
        [dispatch, exchangeToken],
    );

    const handleResendCode = useCallback(() => {
        dispatch(recoveryPasswordActions.backToStep1());
    }, [dispatch]);

    const getLabels = useCallback((id) => {
        const keys = {
            resetCode: `${FORM_ID}.resetCode.label`,
            continue: "global.continue",
            resendCode: "recoveryPassword.step2.resendMail.link",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback((id) => i18n.get(id), []);

    return (
        <RecoveryPasswordStep2
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            handleResendCode={handleResendCode}
            getLabels={getLabels}
            getText={getText}
            submitLoading={recoveryAsyncPending}
            resendLoading={false}
            fieldErrors={fieldErrors ?? undefined}
        />
    );
}

RecoveryPasswordStep2V3.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    exchangeToken: string,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
};

RecoveryPasswordStep2V3.defaultProps = {
    exchangeToken: undefined,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
};

const mapStateToProps = (state) => ({
    exchangeToken: recoveryPasswordSelectors.getExchangeToken(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
});

export default compose(connect(mapStateToProps), withExchangeToken)(RecoveryPasswordStep2V3);
